import type { FeatureFlagId, WorkspaceFeatureFlags } from '@/external/db/types';

// Pure feature-flag registry + resolution, shared by the client provider
// (feature-flags.tsx) and server code (the back-office route). No React, no
// 'use client', so it is safe to import from a route handler. The id union and
// the WorkspaceFeatureFlags shape live in external/db/types (they type the
// workspace_feature_flags.flags jsonb column).

export type { FeatureFlagId, WorkspaceFeatureFlags };

export interface FeatureFlagDef {
  id: FeatureFlagId;
  label: string;
  description: string;
  defaultOn: boolean;
  // When true, the flag defaults OFF in the KPC workspace even though defaultOn
  // is true elsewhere. A workspace override still wins.
  hiddenByDefaultInKpc: boolean;
  // When true, the flag defaults ON in the KPC workspace even though defaultOn
  // is false elsewhere — so a kPlanConnect tenant gets the tailored experience
  // without anyone toggling it. A workspace override still wins.
  defaultOnInKpc: boolean;
}

export const FEATURE_FLAGS: FeatureFlagDef[] = [
  {
    id: 'kpc_cobranding',
    label: 'KPC co-branding',
    description:
      'KPlanConnect demo mode: co-branding next to Matilda in the navbar, plus a payroll-focused tailoring of the app — trimmed Clients list, payroll-first client view, and staff/dev surfaces (fact card, Shuttle link, admin launcher, integration settings) hidden.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: true,
  },
  {
    id: 'partners',
    label: 'Partners',
    description:
      'Advisor + CPA referral dashboards, the /partners workspace, the CRM Partners panel, and Matilda magical compose.',
    defaultOn: true,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'kpc_branding',
    label: 'kPlanConnect co-branding',
    description:
      'Show the "× Kplan connect" lockup in the navbar, the same lockup on the partner-portal chrome, and "KPlanConnect" copy on the expired-link page. Turn off for non-KPC demos.',
    defaultOn: true,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_auto_launch',
    label: 'Schwab payroll auto-launch',
    description:
      'Launch a Schwab payroll for trading automatically once it parks after a clean submit, instead of waiting for a manual launch click on the admin Schwab payroll page. Off = manual launch (the default everywhere).',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'schwab_payroll_halt',
    label: 'Schwab payroll kill switch',
    description:
      'Halt every Schwab payroll submission and launch across the workspace. Checked between every step — flip on to stop the pipeline dead.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'srt_setup_halt',
    label: 'SRT setup kill switch',
    description:
      'Halt SRT Plan Maintenance setup sessions. Checked before each plan session and between items — flip on to stop the sweep dead.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'srt_setup_paused',
    label: 'SRT setup paused',
    description:
      'Pause the SRT sweep so a human can use Citrix: the worker finishes its current item, releases the operator session, and leaves remaining plans to resume. Unlike the kill switch, resuming picks up where it left off.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
  {
    id: 'hide_unavailable',
    label: 'Hide unavailable integrations',
    description:
      'Hide integrations whose external credentials/egress are not provisioned in this environment (Zoho, HubSpot, FTWilliam, DocuSign, AgentMail provisioning) so they cannot be reached from the UI. Off everywhere by default — flip on for demo/pentest workspaces that should not surface unconfigured connectors.',
    defaultOn: false,
    hiddenByDefaultInKpc: false,
    defaultOnInKpc: false,
  },
];

function defaultFor(def: FeatureFlagDef, isKpc: boolean): boolean {
  if (isKpc && def.defaultOnInKpc) return true;
  if (isKpc && def.hiddenByDefaultInKpc) return false;
  return def.defaultOn;
}

export function defaultFlagsFor(isKpc: boolean): WorkspaceFeatureFlags {
  return Object.fromEntries(
    FEATURE_FLAGS.map(def => [def.id, defaultFor(def, isKpc)]),
  ) as WorkspaceFeatureFlags;
}

// The full effective config: stored per-workspace overrides win over the
// code-computed defaults.
export function resolveFlags(
  isKpc: boolean,
  overrides: Partial<WorkspaceFeatureFlags>,
): WorkspaceFeatureFlags {
  return { ...defaultFlagsFor(isKpc), ...overrides };
}
